import React, { useState, useEffect, useCallback } from 'react';
import { X, RefreshCcw, CheckCircle2, AlertCircle } from 'lucide-react';
import gateSystemsService from '../services/gateSystemsService';

/**
 * GateSystemConfigDrawer — per-facility gate-system configuration.
 *
 * Renders the provider, code rules, access hours, gate location note,
 * and a "Test connection" action. Closes on Save or Cancel. The parent
 * page is responsible for showing/hiding this drawer and refreshing its
 * facility list after a save.
 *
 * Currently the only available provider is "manual" (operator-mediated).
 * Future providers (PTI Falcon API, EasyCode CSV drop, Noke, OpenPath)
 * register in the backend integrations.gate_systems package and will
 * appear in the dropdown automatically once the backend lists them.
 */

// `available: false` = the backend registry has no implementation for this
// key yet (integrations/gate_systems/registry.py ships "manual" only). These
// stay listed so the roadmap is visible, but they are NOT selectable: a
// facility saved with an unimplemented provider used to break move-in gate
// provisioning outright — get_provider() raised, the tenant got no move-in
// email, and no operator task was created. Move a provider up to
// `available: true` in the same PR that registers it server-side.
export const PROVIDERS = [
  {
    key: 'manual',
    label: 'Manual (operator-mediated)',
    hint: 'When a tenant pays, you\'ll get a task to create their code in your gate system, then paste it back here.',
    available: true,
  },
  {
    key: 'nimbio',
    label: 'Nimbio (cellular gate access)',
    hint: 'Nimbio Access FN1. Tenants are members identified by phone and hold digital keys — no keypad codes. mySMS360 grants, disables and revokes those keys, and can open the gate on demand.',
    available: true,
    // No keypad: nothing to mint, and the move-in email must not promise a
    // code that does not exist. Selecting this provider turns codes off.
    codeless: true,
  },
  // Display-only until the backend registry can build them. Their keys are
  // NOT in the GateProvider enum, which is safe precisely because a
  // disabled option can never be submitted — and the rule in this file is
  // that a facility saved with a provider the registry cannot construct
  // gets no gate code, no move-in email and no operator task.
  {
    key: 'pti_storlogix',
    label: 'PTI StorLogix',
    hint: 'Plug-and-play PTI kit. Not built yet — a PTI facility is provisioned manually today.',
    available: false,
  },
  {
    key: 'pdk',
    label: 'PDK',
    hint: 'Plug-and-play PDK kit. Not built yet.',
    available: false,
  },
  {
    key: 'custom',
    label: 'Custom integration (your own script)',
    hint: 'mySMS360 exposes each tenant\'s gate code + lockout state via API; your own script enforces it at the gate.',
    available: false,
  },
  {
    key: 'cloud_controller',
    label: 'Other cloud controller',
    hint: 'A hosted access-control cloud. mySMS360 mints the tenant\'s code and pushes it to the provider\'s API, then keeps it in sync on move-out/lockout.',
    available: false,
  },
  {
    key: 'boron_particle',
    label: 'Boron Particle (in-house 4G)',
    hint: 'In-house Particle controller that validates codes against mySMS360 live.',
    available: false,
  },
];

// How mySMS360 MINTS a tenant's gate code at move-in. Phone-derived is the
// default: a code the tenant already knows by heart is the one they can use
// at the keypad without looking it up. Code length comes from the Code length
// field — "last N of phone" follows it, so length 5 = last 5 digits.
// 'sequential' was listed but never implemented; it fell back to random
// silently, so it's gone rather than pretending.
const CODE_STRATEGIES = [
  { key: 'phone_last4', label: 'Last digits of phone (uses Code length)' },
  { key: 'random', label: 'Random (cryptographically secure)' },
];

const defaultConfig = {
  provider: 'manual',
  config: {},
  codes_enabled: true,
  code_length: 4,
  code_strategy: 'random',
  access_hours: { mode: '24h', start: '06:00', end: '22:00' },
  gate_location_note: '',
  is_active: true,
  lockout_after_days: null,
};

const GateSystemConfigDrawer = ({ facilityId, facilityName, onClose, onSaved }) => {
  const [cfg, setCfg] = useState(defaultConfig);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [healthcheck, setHealthcheck] = useState(null);
  const [hcLoading, setHcLoading] = useState(false);

  const load = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    try {
      const res = await gateSystemsService.getConfig(facilityId);
      if (res.data) {
        setCfg({
          ...defaultConfig,
          ...res.data,
          access_hours: { ...defaultConfig.access_hours, ...(res.data.access_hours || {}) },
        });
      } else {
        setCfg(defaultConfig);
      }
    } catch (err) {
      console.error('Failed to load gate config:', err);
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => { load(); }, [load]);

  const setField = (key, value) => setCfg((prev) => ({ ...prev, [key]: value }));
  const setHours = (key, value) =>
    setCfg((prev) => ({
      ...prev,
      access_hours: { ...prev.access_hours, [key]: value },
    }));

  const save = async () => {
    setSaving(true);
    try {
      await gateSystemsService.putConfig(facilityId, cfg);
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to save gate config');
    } finally {
      setSaving(false);
    }
  };

  const runHealthcheck = async () => {
    setHcLoading(true);
    try {
      const res = await gateSystemsService.healthcheck(facilityId);
      setHealthcheck(res.data);
    } catch (err) {
      setHealthcheck({
        status: 'error',
        message: err?.response?.data?.detail || 'Healthcheck failed',
      });
    } finally {
      setHcLoading(false);
    }
  };

  const providerMeta = PROVIDERS.find((p) => p.key === cfg.provider) || PROVIDERS[0];

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 bg-black/30 flex justify-end">
        <div className="bg-white w-full max-w-lg p-6">Loading...</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex justify-end">
      <div className="bg-white w-full max-w-lg h-full overflow-y-auto p-6">
        <div className="flex items-start justify-between mb-5">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              Gate System Configuration
            </h2>
            <p className="text-[13px] text-slate-500">{facilityName || 'Facility'}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-700 rounded"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-5">
          <div>
            <label className="block text-[13px] font-medium text-slate-700 mb-1.5">
              Provider
            </label>
            <select
              value={cfg.provider}
              onChange={(e) => {
                const next = PROVIDERS.find((p) => p.key === e.target.value);
                // A codeless provider has no gate codes; this flag is what
                // decides which move-in email a tenant gets, so it must
                // follow the provider rather than stay at its old value.
                setCfg((prev) => ({
                  ...prev,
                  provider: e.target.value,
                  ...(next?.codeless ? { codes_enabled: false } : {}),
                }));
              }}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px]"
            >
              {PROVIDERS.map((p) => (
                <option key={p.key} value={p.key} disabled={!p.available}>
                  {p.label}{p.available ? '' : ' — coming soon'}
                </option>
              ))}
            </select>
            {providerMeta?.hint && (
              <p className="mt-1 text-[12px] text-slate-500">{providerMeta.hint}</p>
            )}
          </div>

          {/* Nimbio credentials. Kept here rather than in a vendor modal so
              the whole gate setup stays one screen. The key is stored by the
              backend and never returned; blank means "keep the stored one".
              Nimbio ships in SHADOW MODE by default — it reports and opens
              on demand but never grants or revokes a real tenant's access
              until someone turns that off deliberately. */}
          {cfg.provider === 'nimbio' && (
            <div className="rounded-lg border border-slate-200 p-3 space-y-3">
              <p className="text-[12px] text-slate-600">
                From your Nimbio admin portal. Use a <strong>test</strong> key
                (<code>nimbio_test_…</code>) while you trial the controller.
              </p>
              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-1">
                  Tenant API key
                </label>
                <input
                  type="text"
                  value={cfg.config?.api_key || ''}
                  onChange={(e) => setField('config', { ...(cfg.config || {}), api_key: e.target.value })}
                  placeholder="nimbio_test_9f2c…a7d1"
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] font-mono"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-medium text-slate-500 mb-1">
                    Latch id (the gate)
                  </label>
                  <input
                    type="text"
                    value={cfg.config?.latch_id || ''}
                    onChange={(e) => setField('config', { ...(cfg.config || {}), latch_id: e.target.value })}
                    placeholder="latch_1"
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] font-mono"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-slate-500 mb-1">
                    Key id(s), comma separated
                  </label>
                  <input
                    type="text"
                    value={(cfg.config?.key_ids || []).join(', ')}
                    onChange={(e) => setField('config', {
                      ...(cfg.config || {}),
                      key_ids: e.target.value.split(',').map((k) => k.trim()).filter(Boolean),
                    })}
                    placeholder="key_main_gate"
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] font-mono"
                  />
                </div>
              </div>
              {/* A key minted on Nimbio's dev server does not authenticate
                  against their production API, and the failure reads like a
                  bad credential rather than a wrong host — so the host has to
                  be settable, not just assumed. */}
              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-1">
                  API host (leave blank for Nimbio production)
                </label>
                <input
                  type="text"
                  value={cfg.config?.base_url || ''}
                  onChange={(e) => setField('config', { ...(cfg.config || {}), base_url: e.target.value.trim() })}
                  placeholder="https://api.nimbio.com"
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] font-mono"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Use <code>https://api.nimbio.dev</code> with a dev-server key.
                </p>
              </div>
              <label className="flex items-start gap-2 text-[12px] text-slate-600">
                <input
                  type="checkbox"
                  checked={cfg.config?.shadow_mode !== false}
                  onChange={(e) => setField('config', { ...(cfg.config || {}), shadow_mode: e.target.checked })}
                  className="mt-0.5"
                />
                <span>
                  <strong>Trial mode</strong> — report and open on demand, but
                  never grant, disable or revoke a tenant&rsquo;s access. Leave
                  this on until Nimbio is the system of record at this facility.
                </span>
              </label>
              {/* Two independent rails. Trial mode (above) protects TENANTS:
                  no grant/disable/revoke. This one protects the KEY: a
                  nimbio_live_ key is refused at save time unless someone says
                  so here, so production can never be pointed at a live
                  credential by accident. A test key simulates opens — the
                  relay does not physically fire — so hearing the click on a
                  bench needs a live key, and on a bench with nothing landed on
                  the operator a real open is harmless. */}
              <label className="flex items-start gap-2 text-[12px] text-slate-600">
                <input
                  type="checkbox"
                  checked={!!cfg.config?.allow_live}
                  onChange={(e) => setField('config', { ...(cfg.config || {}), allow_live: e.target.checked })}
                  className="mt-0.5"
                />
                <span>
                  <strong>Accept a live key</strong> — required for a
                  <code> nimbio_live_</code> key. Test keys simulate opens (the
                  relay does not fire); a live key fires it for real. Trial
                  mode above still applies, so tenant access stays untouched
                  either way.
                </span>
              </label>
            </div>
          )}

          {/* Code minting only exists for providers with a keypad. A codeless
              provider (Nimbio: tenants hold digital keys by phone) has nothing
              to mint, and showing the fields invites a length or strategy that
              means nothing there. */}
          {!providerMeta.codeless && (
          <>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[13px] font-medium text-slate-700 mb-1.5">
                Code length
              </label>
              <input
                type="number"
                min={3}
                max={10}
                value={cfg.code_length}
                onChange={(e) => setField('code_length', Number(e.target.value))}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px]"
              />
            </div>
            <div>
              <label className="block text-[13px] font-medium text-slate-700 mb-1.5">
                Code strategy
              </label>
              <select
                value={cfg.code_strategy}
                onChange={(e) => setField('code_strategy', e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px]"
              >
                {CODE_STRATEGIES.map((s) => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </select>
            </div>
          </div>
          <p className="text-[12px] text-slate-500 -mt-1">
            {cfg.code_strategy === 'random'
              ? `Each tenant gets a random ${cfg.code_length}-digit code at move-in.`
              : `Each tenant's code is the last ${cfg.code_length} digits of their `
                + 'phone number (random fallback if the phone is missing or the '
                + 'code is already taken).'}
            {' '}Codes are minted automatically when the tenant pays — nobody
            has to type one in.
          </p>
          </>
          )}

          <div>
            <label className="block text-[13px] font-medium text-slate-700 mb-1.5">
              Access hours
            </label>
            <div className="flex items-center gap-3 mb-2">
              <label className="flex items-center gap-1.5 text-[13px] text-slate-600">
                <input
                  type="radio"
                  checked={cfg.access_hours.mode === '24h'}
                  onChange={() => setHours('mode', '24h')}
                />
                24-hour access
              </label>
              <label className="flex items-center gap-1.5 text-[13px] text-slate-600">
                <input
                  type="radio"
                  checked={cfg.access_hours.mode === 'window'}
                  onChange={() => setHours('mode', 'window')}
                />
                Custom window
              </label>
            </div>
            {cfg.access_hours.mode === 'window' && (
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="time"
                  value={cfg.access_hours.start || '06:00'}
                  onChange={(e) => setHours('start', e.target.value)}
                  className="px-3 py-2 rounded-lg border border-slate-200 text-[13px]"
                />
                <input
                  type="time"
                  value={cfg.access_hours.end || '22:00'}
                  onChange={(e) => setHours('end', e.target.value)}
                  className="px-3 py-2 rounded-lg border border-slate-200 text-[13px]"
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-[13px] font-medium text-slate-700 mb-1.5">
              Gate location note
            </label>
            <textarea
              value={cfg.gate_location_note || ''}
              onChange={(e) => setField('gate_location_note', e.target.value)}
              placeholder="Tell tenants where to find the gate and how to use it. Shown in their welcome email."
              rows={3}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px]"
            />
          </div>

          <div>
            <label className="flex items-center gap-1.5 text-[13px] text-slate-700">
              <input
                type="checkbox"
                checked={!!cfg.is_active}
                onChange={(e) => setField('is_active', e.target.checked)}
              />
              Active (uncheck to pause provisioning)
            </label>
          </div>

          {/* Explicit gated-vs-gateless choice (owner ask 2026-07-30):
              which move-in email tenants get. Unchecked = no codes ever
              minted and the no-code welcome is sent — even if stale codes
              linger on units. Saving the drawer records the choice
              explicitly (before this, "gateless" was merely inferred from
              never having configured this screen). */}
          <div>
            <label className="flex items-center gap-1.5 text-[13px] text-slate-700">
              <input
                type="checkbox"
                checked={cfg.codes_enabled !== false}
                onChange={(e) => setField('codes_enabled', e.target.checked)}
              />
              This facility uses gate access codes
            </label>
            <p className="text-[11px] text-slate-400 mt-0.5 ml-5">
              Unchecked: tenants get the move-in email without any gate-code
              section, and no codes are created.
            </p>
          </div>

          <div className="border-t border-slate-200 pt-4">
            <label className="block text-[13px] font-medium text-slate-700 mb-1.5">
              Auto-lockout after (days past due)
            </label>
            <input
              type="number"
              min={1}
              value={cfg.lockout_after_days ?? ''}
              onChange={(e) =>
                setField(
                  'lockout_after_days',
                  e.target.value === '' ? null : Number(e.target.value),
                )
              }
              placeholder="Blank = manual lockouts only"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px]"
            />
            <p className="mt-1 text-[12px] text-slate-500">
              Days past the rent due date before a delinquent tenant is locked
              out of the gate. Blank disables auto-lockout. Applies to new
              (website/operator) tenants only — legacy imports are never
              auto-locked.
            </p>
          </div>

          <div className="border-t border-slate-200 pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[13px] font-medium text-slate-700">Test connection</p>
                <p className="text-[12px] text-slate-500">
                  Verifies the provider can be reached.
                </p>
              </div>
              <button
                type="button"
                onClick={runHealthcheck}
                disabled={hcLoading}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 text-[13px] text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                <RefreshCcw className={`h-3.5 w-3.5 ${hcLoading ? 'animate-spin' : ''}`} />
                Run check
              </button>
            </div>
            {healthcheck && (
              <div className={`mt-2 flex items-start gap-2 text-[12px] ${
                healthcheck.status === 'ok' ? 'text-emerald-700' : 'text-red-700'
              }`}>
                {healthcheck.status === 'ok' ? (
                  <CheckCircle2 className="h-3.5 w-3.5 mt-0.5" />
                ) : (
                  <AlertCircle className="h-3.5 w-3.5 mt-0.5" />
                )}
                <span>{healthcheck.message}</span>
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex items-center gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-slate-200 text-[13px] text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white text-[13px] hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GateSystemConfigDrawer;
